import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { withTranslation } from 'react-i18next'

import { Column } from '../../../..'
import {
    AudioThumbnail,
    BackgroundPlayIcon,
    Body,
    ExclamationIconInfo,
    IconWrapper,
    InfoIcon,
    LoadingIcon,
    MediaItemDom,
    MediaTypeIcon,
    PlayIcon,
    SelectedIcon,
} from './MediaItem.style'
import DEFAULT_SRC from '../../../../../resources/mediaThumbnails/default.png'
import { Types as MediaTypes } from '../../../../../actions/content/media'
import * as ContentActions from '../../../../../actions/content'
import Footer from './Footer'
import LeftColumn from './LeftColumn'
import RightColumn from './RightColumn'
import { EstateUtils, MediaUtils } from '../../../../../utils'
import { Media } from '../../../../../constants'
import { withSize } from '../../../../utils'

class MediaItem extends Component {
    state = {
        content: null,
        loading: true,
        thumbnail: null,
    }

    componentDidMount() {
        this.load()
    }

    componentDidUpdate(prevProps) {
        const {
            media: { id },
        } = this.props
        const {
            media: { id: prevId },
        } = prevProps
        if (id !== prevId) {
            this.load()
        }
    }

    get isPreviewAvailable() {
        const { media } = this.props

        if (!media) {
            return false
        }

        const { date_upload_started, date_upload_ended } = media
        return (
            (!date_upload_started && !date_upload_ended) || // this sends true if both dates are null (old entries)
            date_upload_ended // upload ended
        )
    }

    get isVideo() {
        const { media } = this.props
        return MediaUtils.isVideo(media)
    }

    get thumbnailSource() {
        const { content, thumbnail } = this.state
        return (this.isVideo ? thumbnail : content) || DEFAULT_SRC
    }

    get typeIcon() {
        const { media = {} } = this.props
        const { type } = media || {}

        if (!type) {
            return null
        }

        if (type.includes('video')) {
            return 'file-video'
        }

        if (type.includes('image')) {
            return 'file-image'
        }

        if (type.includes('audio')) {
            return 'file-audio'
        }

        return null
    }

    load = async () => {
        const { isVideo } = this
        this.setState({ thumbnail: null, loading: true }, () => {
            if (isVideo) {
                this.downloadThumbnail()
            } else {
                this.downloadMedia()
            }
        })
    }

    downloadThumbnail = async () => {
        const { media, downloadThumbnail } = this.props

        if (!media) {
            return null
        }

        const response = await downloadThumbnail(media.id)

        if (response.type === MediaTypes.DOWNLOAD_MEDIA_THUMBNAIL_SUCCESS) {
            this.setState({ thumbnail: response.payload })
        }
        this.setState({ loading: false })
    }

    downloadMedia = async () => {
        const { media, downloadMedia } = this.props
        const response = await downloadMedia(media.id, true)

        if (response.type === MediaTypes.DOWNLOAD_MEDIA_SUCCESS) {
            this.setState({ content: response.payload })
        }
        this.setState({ loading: false })
    }

    getPushStatus = () => {
        const { media = {} } = this.props
        const { media_push_status: mediaPushStatus = [] } = media

        if (!mediaPushStatus.length) {
            return 'UNKNOWN'
        }

        const { status } = mediaPushStatus[0]

        return status
    }

    render() {
        const { loading } = this.state
        const {
            inPlaylist,
            inSelectedPlaylist,
            isDownloadBlocked,
            isMobile,
            media,
            onDownloadMedia,
            onEdit,
            onInspectMedia,
            onRemoveMedia,
            onRetryUpload,
            t,
        } = this.props
        const {
            id: mediaId,
            selected,
            is_approved: isApproved,
            is_tested: isTested,
            isWorking, playlists,
            order,
        } = media

        /**
         * On Download Button click (with video player)
         * @param {Object} e
         */
        const onDownloadClickWithPlayer = (e) => {
            if (typeof e.stopPropagation === 'function') {
                e.stopPropagation()
            }

            if (onDownloadMedia) {
                onDownloadMedia(mediaId, true)
            }
        }

        const isBeingUploaded = MediaUtils.isBeingUploaded(media)
        const hasFailedUpload = MediaUtils.hasFailedUpload(media)
        const isVideo = MediaUtils.isVideo(media)
        const isAudio = MediaUtils.isAudio(media)
        const status = this.getPushStatus()
        const isPendingApproval = isApproved === Media.approvalStatus.pending || isTested === Media.testingStatus.pending
        const isRejected = isApproved === Media.approvalStatus.rejected || isTested === Media.testingStatus.rejected
        const hasFailedPush = status === Media.pushStatus.failed

        return (
            <MediaItemDom
                onClick={() => onInspectMedia(mediaId)}
                key={mediaId}
                disabled={isPendingApproval || (isBeingUploaded && !hasFailedUpload) || status === Media.pushStatus.pending}
                alert={hasFailedUpload || hasFailedPush}
            >
                <Column
                    width={isMobile ? '30%' : '25%'}
                    noOverflowX
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        textAlign: 'center',
                        backgroundColor: 'black',
                        overflow: 'hidden',
                        justifyContent: 'center',
                    }}
                >
                    {!isBeingUploaded && (
                        <>
                            {(isVideo || isAudio) && this.isPreviewAvailable && (
                                <BackgroundPlayIcon onClick={onDownloadClickWithPlayer}>
                                    <IconWrapper>{!loading ? <PlayIcon icon="play" /> : <LoadingIcon icon="spinner" pulse spin />}</IconWrapper>
                                </BackgroundPlayIcon>
                            )}
                            {this.typeIcon && (
                                <MediaTypeIcon>
                                    <FontAwesomeIcon icon={this.typeIcon} title={t(this.typeIcon)} />
                                </MediaTypeIcon>
                            )}
                            {!isAudio && (
                                <img
                                    src={this.thumbnailSource}
                                    alt="Media thumbnail"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                    }}
                                />
                            )}
                            {isAudio && (
                                <AudioThumbnail>
                                    <FontAwesomeIcon icon="music" />
                                </AudioThumbnail>
                            )}
                        </>
                    )}
                    {isBeingUploaded && !hasFailedUpload && (
                        <>
                            <InfoIcon title={t('media-still-processed')} />
                            <IconWrapper>
                                <LoadingIcon icon="spinner" pulse spin big />
                            </IconWrapper>
                        </>
                    )}
                    {isBeingUploaded && hasFailedUpload && <ExclamationIconInfo alert={hasFailedUpload} title={t('media-failed-processing')} />}
                    <SelectedIcon selected={selected} />
                </Column>
                <Column
                    width={isMobile ? '70%' : '75%'}
                    style={{
                        textAlign: 'center',
                        overflowY: 'hidden',
                        position: 'absolute',
                    }}
                >
                    <Body>
                        <LeftColumn media={media} isBeingUploaded={isBeingUploaded} />
                        <RightColumn
                            inSelectedPlaylist={inSelectedPlaylist}
                            isDownloadBlocked={isDownloadBlocked}
                            isBeingUploaded={isBeingUploaded}
                            media={media}
                            onEdit={onEdit}
                            onRemoveMedia={onRemoveMedia}
                            onDownloadMedia={onDownloadMedia}
                        />
                    </Body>
                    <Footer
                        hasFailedUpload={hasFailedUpload}
                        id={mediaId}
                        inPlaylist={inPlaylist}
                        isApproved={!isPendingApproval}
                        isBeingUploaded={isBeingUploaded}
                        isNotPushed={status === Media.pushStatus.pending}
                        isRejected={isRejected}
                        isWorking={isWorking}
                        onInspectMedia={onInspectMedia}
                        onRetryUpload={onRetryUpload}
                        playlists={playlists}
                        pushedHasFailed={status === Media.pushStatus.failed}
                        order={order}
                    />
                </Column>
            </MediaItemDom>
        )
    }
}

MediaItem.propTypes = {
    downloadMedia: PropTypes.func.isRequired,
    downloadThumbnail: PropTypes.func.isRequired,
    inPlaylist: PropTypes.number,
    inSelectedPlaylist: PropTypes.bool,
    isDownloadBlocked: PropTypes.bool,
    media: PropTypes.shape({}),
    onDownloadMedia: PropTypes.func,
    onEdit: PropTypes.func,
    onInspectMedia: PropTypes.func,
    onRemoveMedia: PropTypes.func,
    onRetryUpload: PropTypes.func,
    selectedPlaylist: PropTypes.shape({}),
    t: PropTypes.func.isRequired,
}

MediaItem.defaultProps = {
    inPlaylist: null,
    inSelectedPlaylist: false,
    isDownloadBlocked: false,
    media: null,
    onDownloadMedia: () => null,
    onEdit: () => null,
    onInspectMedia: () => null,
    onRemoveMedia: () => null,
    onRetryUpload: () => null,
    selectedPlaylist: {},
}

function mapStateToProps(state) {
    return {
        estateAPI: EstateUtils.getAPI(state),
    }
}

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...ContentActions,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withSize(MediaItem)))
